import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getBusinessClients, updateBusinessClient } from '../../services/businessService';
import type { ClientData, ClientContact } from '../../services/businessService';
import { FaUserPlus, FaEdit, FaTimes, FaSave, FaUpload, FaTrash, FaLinkedin, FaPlus } from 'react-icons/fa';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../config/firebase';

const Clients: React.FC = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const [clients, setClients] = useState<ClientData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState<{ open: boolean, client: ClientData | null }>({ open: false, client: null });
  const [showEditModal, setShowEditModal] = useState<{ open: boolean, client: ClientData | null }>({ open: false, client: null });
  const [editFormData, setEditFormData] = useState<Partial<ClientData>>({});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'contacts' | 'logo'>('general');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [newContact, setNewContact] = useState<Omit<ClientContact, 'id'>>({
    name: '',
    email: '',
    phone: '',
    linkedinUrl: ''
  });
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<ClientContact | null>(null);
  const [contactModalTab, setContactModalTab] = useState<'info' | 'linkedin'>('info');
  const [editingContact, setEditingContact] = useState(false);
  const [editContactData, setEditContactData] = useState<ClientContact | null>(null);

  useEffect(() => {
    const fetchClients = async () => {
      setLoading(true);
      setError(null);
      try {
        // Usar el businessId real del usuario
        const data = await getBusinessClients(userData?.businessId ?? '');
        setClients(data);
      } catch (err) {
        setError(t('errors.fetchClients'));
      } finally {
        setLoading(false);
      }
    };
    if (userData?.businessId) fetchClients();
  }, [userData, t]);

  const handleViewClick = (client: ClientData) => {
    setShowViewModal({ open: true, client });
    setActiveTab('general');
  };

  const handleOpenEditMode = () => {
    if (!showViewModal.client) return;
    
    setEditFormData({
      name: showViewModal.client.name,
      email: showViewModal.client.email,
      phone: showViewModal.client.phone,
      address: showViewModal.client.address,
      logo: showViewModal.client.logo,
      contacts: showViewModal.client.contacts || []
    });
    setShowViewModal({ open: false, client: null });
    setShowEditModal({ open: true, client: showViewModal.client });
  };

  const handleCloseViewModal = () => {
    setShowViewModal({ open: false, client: null });
    setActiveTab('general');
  };

  const handleSaveEdit = async () => {
    if (!showEditModal.client || !userData?.businessId) return;

    try {
      setSaving(true);
      await updateBusinessClient(userData.businessId, showEditModal.client.id, editFormData);
      
      // Actualizar la lista local
      setClients(clients.map(c => 
        c.id === showEditModal.client!.id 
          ? { ...c, ...editFormData }
          : c
      ));
      
      setShowEditModal({ open: false, client: null });
      setEditFormData({});
    } catch (err) {
      console.error('Error updating client:', err);
      setError(t('errors.updateClient', 'Error al actualizar cliente'));
    } finally {
      setSaving(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal({ open: false, client: null });
    setEditFormData({});
    setActiveTab('general');
    setUploadingLogo(false);
    setNewContact({ name: '', email: '', phone: '', linkedinUrl: '' });
  };

  const handleAddContact = () => {
    if (!newContact.name || !newContact.email) {
      setError(t('errors.contactNameEmailRequired', 'El nombre y email del contacto son requeridos'));
      return;
    }

    const contact: ClientContact = {
      id: `contact_${Date.now()}`,
      ...newContact
    };

    const updatedContacts = [...(editFormData.contacts || []), contact];
    setEditFormData({ ...editFormData, contacts: updatedContacts });
    setNewContact({ name: '', email: '', phone: '', linkedinUrl: '' });
  };

  const handleRemoveContact = (contactId: string) => {
    const updatedContacts = (editFormData.contacts || []).filter(c => c.id !== contactId);
    setEditFormData({ ...editFormData, contacts: updatedContacts });
  };

  const handleViewContact = (contact: ClientContact) => {
    console.log('Opening contact modal for:', contact);
    setSelectedContact(contact);
    setContactModalTab('info');
    setShowContactModal(true);
  };

  const handleCloseContactModal = () => {
    setShowContactModal(false);
    setSelectedContact(null);
    setContactModalTab('info');
    setEditingContact(false);
    setEditContactData(null);
  };

  const handleEditContact = () => {
    if (selectedContact) {
      setEditContactData({ ...selectedContact });
      setEditingContact(true);
    }
  };

  const handleSaveContactEdit = () => {
    if (!editContactData) return;

    const updatedContacts = (editFormData.contacts || []).map(c => 
      c.id === editContactData.id ? editContactData : c
    );
    setEditFormData({ ...editFormData, contacts: updatedContacts });
    setSelectedContact(editContactData);
    setEditingContact(false);
    setEditContactData(null);
  };

  const handleCancelContactEdit = () => {
    setEditingContact(false);
    setEditContactData(null);
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !showEditModal.client || !userData?.businessId) return;

    // Validar que sea PNG
    if (file.type !== 'image/png') {
      setError(t('errors.onlyPngAllowed', 'Solo se permiten archivos PNG'));
      return;
    }

    // Validar tamaño (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError(t('errors.fileTooLarge', 'El archivo es demasiado grande (máx. 2MB)'));
      return;
    }

    try {
      setUploadingLogo(true);
      setError(null);

      // Crear referencia en Storage
      const timestamp = Date.now();
      const storageRef = ref(storage, `clients/${userData.businessId}/${showEditModal.client.id}/logo_${timestamp}.png`);
      
      // Subir archivo
      await uploadBytes(storageRef, file);
      
      // Obtener URL de descarga
      const downloadURL = await getDownloadURL(storageRef);
      
      // Actualizar el form data
      setEditFormData({ ...editFormData, logo: downloadURL });
      
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError(t('errors.logoUpload', 'Error al subir el logo'));
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveLogo = async () => {
    if (!editFormData.logo) return;

    try {
      setUploadingLogo(true);
      
      // Si es una URL de Firebase Storage, intentar eliminar el archivo
      if (editFormData.logo.includes('firebase')) {
        try {
          const logoRef = ref(storage, editFormData.logo);
          await deleteObject(logoRef);
        } catch (err) {
          console.log('Could not delete logo file:', err);
        }
      }
      
      // Actualizar el form data
      setEditFormData({ ...editFormData, logo: undefined });
      
    } catch (err) {
      console.error('Error removing logo:', err);
      setError(t('errors.logoRemove', 'Error al eliminar el logo'));
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-4 md:p-8 font-sans">
      <div className="max-w-7xl mx-auto">
        {/* Header mejorado */}
        <div className="bg-white rounded-3xl shadow-xl border border-white/40 p-6 md:p-8 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text text-transparent mb-2">
                {t('business.clients', 'Clientes')}
              </h1>
              <p className="text-gray-600">
                {clients.length} {clients.length === 1 ? t('business.clientRegistered', 'cliente registrado') : t('business.clientsRegistered', 'clientes registrados')}
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <FaPlus className="text-lg" />
              {t('business.addClient', 'Nuevo cliente')}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">{t('common.loading')}</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8 text-center">
            <div className="text-red-500 text-lg font-semibold">{error}</div>
          </div>
        ) : clients.length === 0 ? (
          <div className="bg-white rounded-3xl p-16 text-center shadow-xl border border-white/40">
            <div className="max-w-md mx-auto">
              <div className="w-32 h-32 bg-gradient-to-br from-red-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                <FaUserPlus className="text-6xl text-red-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('business.noClients')}</h3>
              <p className="text-gray-500 mb-6">{t('business.addFirstClientMessage', 'Comienza agregando tu primer cliente para gestionar proyectos y contactos')}</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
              >
                <FaPlus />
                {t('business.addFirstClient', 'Agregar primer cliente')}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clients.map(client => (
              <div 
                key={client.id} 
                className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl border border-gray-100 hover:border-red-200 transition-all cursor-pointer transform hover:scale-105 relative overflow-hidden"
                onClick={() => handleViewClick(client)}
              >
                {/* Decoración de fondo */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-red-50 to-pink-50 rounded-full -mr-16 -mt-16 opacity-50 group-hover:opacity-100 transition-opacity"></div>
                
                <div className="relative">
                  {/* Logo o inicial del cliente */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {client.logo ? (
                        <img 
                          src={client.logo} 
                          alt={client.name}
                          className="w-12 h-12 rounded-lg object-cover shadow-md border-2 border-white"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-500 rounded-lg flex items-center justify-center shadow-md">
                          <span className="text-white text-xl font-bold">
                            {client.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        <h3 className="font-bold text-gray-900 text-lg group-hover:text-red-600 transition-colors">
                          {client.name}
                        </h3>
                        {client.contacts && client.contacts.length > 0 && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">
                            {client.contacts.length} {client.contacts.length === 1 ? t('business.contact', 'contacto') : t('business.contactsPlural', 'contactos')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Información del cliente */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="text-lg">📧</span>
                      <span className="truncate">{client.email}</span>
                    </div>
                    {client.phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="text-lg">📱</span>
                        <span>{client.phone}</span>
                      </div>
                    )}
                    {client.address && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="text-lg">📍</span>
                        <span className="truncate">{client.address}</span>
                      </div>
                    )}
                  </div>

                  {/* Footer con fecha */}
                  <div className="pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>
                        {client.createdAt?.toDate ? 
                          `${t('business.addedOn', 'Agregado')} ${client.createdAt.toDate().toLocaleDateString()}` : 
                          t('business.dateNotAvailable', 'Fecha no disponible')
                        }
                      </span>
                      <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center group-hover:bg-red-200 transition-colors">
                        <span className="text-red-600 text-xs">→</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Modal de visualización del cliente */}
        {showViewModal.open && showViewModal.client && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">{showViewModal.client.name}</h3>
                <button
                  onClick={handleCloseViewModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FaTimes size={24} />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-200 mb-6">
                <button
                  onClick={() => setActiveTab('general')}
                  className={`px-6 py-3 font-medium transition-all ${
                    activeTab === 'general'
                      ? 'border-b-2 border-red-500 text-red-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('business.generalInfo', 'Información General')}
                </button>
                <button
                  onClick={() => setActiveTab('contacts')}
                  className={`px-6 py-3 font-medium transition-all ${
                    activeTab === 'contacts'
                      ? 'border-b-2 border-red-500 text-red-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('business.contacts', 'Contactos')} ({showViewModal.client.contacts?.length || 0})
                </button>
                <button
                  onClick={() => setActiveTab('logo')}
                  className={`px-6 py-3 font-medium transition-all ${
                    activeTab === 'logo'
                      ? 'border-b-2 border-red-500 text-red-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Logo
                </button>
              </div>

              {/* Contenido de los tabs */}
              <div className="space-y-4">
                {activeTab === 'general' && (
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-600 mb-2">
                        {t('business.clientName', 'Nombre del cliente')}
                      </label>
                      <p className="text-gray-900 font-medium">{showViewModal.client.name}</p>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-600 mb-2">
                        {t('business.clientEmail', 'Email')}
                      </label>
                      <a href={`mailto:${showViewModal.client.email}`} className="text-blue-600 hover:underline">
                        {showViewModal.client.email}
                      </a>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-600 mb-2">
                        {t('business.clientPhone', 'Teléfono')}
                      </label>
                      <a href={`tel:${showViewModal.client.phone}`} className="text-blue-600 hover:underline">
                        {showViewModal.client.phone}
                      </a>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-600 mb-2">
                        {t('business.clientAddress', 'Dirección')}
                      </label>
                      <p className="text-gray-900">{showViewModal.client.address || '-'}</p>
                    </div>
                  </div>
                )}

                {activeTab === 'contacts' && (
                  <div className="space-y-4">
                    {showViewModal.client.contacts && showViewModal.client.contacts.length > 0 ? (
                      showViewModal.client.contacts.map((contact) => (
                        <div
                          key={contact.id}
                          className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => handleViewContact(contact)}
                        >
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900">{contact.name}</span>
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <div>📧 {contact.email}</div>
                              {contact.phone && <div>📱 {contact.phone}</div>}
                              {contact.linkedinUrl && (
                                <div className="flex items-center gap-1">
                                  <FaLinkedin className="text-blue-600" />
                                  <span className="text-blue-600">LinkedIn</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <FaUserPlus className="text-5xl mx-auto mb-3 text-gray-300" />
                        <p>{t('business.noContacts', 'No hay contactos registrados')}</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'logo' && (
                  <div className="text-center py-8">
                    {showViewModal.client.logo ? (
                      <img
                        src={showViewModal.client.logo}
                        alt="Client Logo"
                        className="w-40 h-40 object-contain rounded-lg shadow-md border-2 border-gray-200 bg-white p-2 mx-auto"
                      />
                    ) : (
                      <div className="w-40 h-40 mx-auto bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                        <FaUserPlus className="text-5xl text-gray-300" />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Botón de editar */}
              <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={handleCloseViewModal}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {t('common.close', 'Cerrar')}
                </button>
                <button
                  onClick={handleOpenEditMode}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  <FaEdit />
                  {t('common.edit', 'Editar')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modales de crear y editar cliente (por implementar) */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-96">
              <h3 className="text-lg font-semibold mb-4">{t('business.addClient', 'Nuevo cliente')}</h3>
              {/* Formulario de creación aquí */}
              <button onClick={() => setShowCreateModal(false)} className="mt-4 px-4 py-2 bg-gray-200 rounded">{t('common.cancel', 'Cancelar')}</button>
            </div>
          </div>
        )}
        {showEditModal.open && showEditModal.client && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">{t('business.editClient', 'Editar cliente')}</h3>
                <button
                  onClick={handleCloseEditModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FaTimes size={24} />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-200 mb-6">
                <button
                  onClick={() => setActiveTab('general')}
                  className={`px-6 py-3 font-medium transition-all ${
                    activeTab === 'general'
                      ? 'border-b-2 border-red-500 text-red-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('business.generalInfo', 'Información General')}
                </button>
                <button
                  onClick={() => setActiveTab('contacts')}
                  className={`px-6 py-3 font-medium transition-all ${
                    activeTab === 'contacts'
                      ? 'border-b-2 border-red-500 text-red-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('business.contacts', 'Contactos')}
                </button>
                <button
                  onClick={() => setActiveTab('logo')}
                  className={`px-6 py-3 font-medium transition-all ${
                    activeTab === 'logo'
                      ? 'border-b-2 border-red-500 text-red-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Logo
                </button>
              </div>

              {/* Tab Content */}
              <div className="space-y-4">
                {activeTab === 'general' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('business.clientName', 'Nombre del cliente')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={editFormData.name || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder={t('business.clientNamePlaceholder', 'Ingrese el nombre')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('business.clientEmail', 'Email')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={editFormData.email || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder={t('business.clientEmailPlaceholder', 'ejemplo@correo.com')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('business.clientPhone', 'Teléfono')}
                      </label>
                      <input
                        type="tel"
                        value={editFormData.phone || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder={t('business.clientPhonePlaceholder', '+1234567890')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('business.clientAddress', 'Dirección')}
                      </label>
                      <textarea
                        value={editFormData.address || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        rows={3}
                        placeholder={t('business.clientAddressPlaceholder', 'Calle, ciudad, país')}
                      />
                    </div>
                  </>
                )}

                {activeTab === 'contacts' && (
                  <div className="space-y-6">
                    {/* Lista de contactos existentes */}
                    {editFormData.contacts && editFormData.contacts.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="font-semibold text-gray-900">
                          {t('business.existingContacts', 'Contactos registrados')}
                        </h4>
                        {editFormData.contacts.map((contact) => (
                          <div
                            key={contact.id}
                            className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md hover:bg-gray-100 transition-all cursor-pointer"
                            onClick={() => {
                              console.log('Click on contact card:', contact);
                              handleViewContact(contact);
                            }}
                          >
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900">{contact.name}</span>
                              </div>
                              <div className="text-sm text-gray-600 space-y-1">
                                <div>📧 {contact.email}</div>
                                {contact.phone && <div>📱 {contact.phone}</div>}
                                {contact.linkedinUrl && (
                                  <div className="flex items-center gap-1">
                                    <FaLinkedin className="text-blue-600" />
                                    <span className="text-blue-600">LinkedIn</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Formulario para agregar nuevo contacto */}
                    <div className="border-t border-gray-200 pt-6">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <FaPlus className="text-green-600" />
                        {t('business.addNewContact', 'Agregar nuevo contacto')}
                      </h4>
                      
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('business.contactName', 'Nombre')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={newContact.name}
                              onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              placeholder={t('business.contactNamePlaceholder', 'Nombre del contacto')}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('business.contactEmail', 'Email')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              value={newContact.email}
                              onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              placeholder={t('business.contactEmailPlaceholder', 'email@ejemplo.com')}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('business.contactPhone', 'Teléfono')}
                            </label>
                            <input
                              type="tel"
                              value={newContact.phone}
                              onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              placeholder={t('business.contactPhonePlaceholder', '+1234567890')}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('business.contactLinkedin', 'LinkedIn URL')}
                            </label>
                            <input
                              type="url"
                              value={newContact.linkedinUrl}
                              onChange={(e) => setNewContact({ ...newContact, linkedinUrl: e.target.value })}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              placeholder="https://linkedin.com/in/..."
                            />
                          </div>
                        </div>

                        <button
                          onClick={handleAddContact}
                          className="w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                        >
                          <FaPlus />
                          {t('business.addContact', 'Agregar contacto')}
                        </button>
                      </div>
                    </div>

                    {editFormData.contacts?.length === 0 && (
                      <div className="text-center py-8 text-gray-400">
                        <FaUserPlus className="text-5xl mx-auto mb-3 text-gray-300" />
                        <p>{t('business.noContacts', 'No hay contactos registrados')}</p>
                        <p className="text-sm">{t('business.addFirstContact', 'Agrega el primer contacto arriba')}</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'logo' && (
                  <div className="space-y-6">
                    {/* Preview del logo */}
                    <div className="text-center">
                      {editFormData.logo ? (
                        <div className="relative inline-block">
                          <img
                            src={editFormData.logo}
                            alt="Client Logo"
                            className="w-40 h-40 object-contain rounded-lg shadow-md border-2 border-gray-200 bg-white p-2"
                          />
                          <button
                            onClick={handleRemoveLogo}
                            disabled={uploadingLogo}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors disabled:opacity-50 shadow-lg"
                            title={t('business.removeLogo', 'Eliminar logo')}
                          >
                            <FaTrash size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="w-40 h-40 mx-auto bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                          <FaUserPlus className="text-5xl text-gray-300" />
                        </div>
                      )}
                    </div>

                    {/* Botón de subida */}
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png"
                        onChange={handleLogoUpload}
                        className="hidden"
                        disabled={uploadingLogo}
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md"
                      >
                        {uploadingLogo ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            {t('common.uploading', 'Subiendo...')}
                          </>
                        ) : (
                          <>
                            <FaUpload />
                            {editFormData.logo 
                              ? t('business.changeLogo', 'Cambiar Logo (PNG)') 
                              : t('business.uploadLogo', 'Subir Logo (PNG)')}
                          </>
                        )}
                      </button>
                    </div>

                    {/* Información y restricciones */}
                    <div className="space-y-3">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800 font-medium mb-2">
                          {t('business.logoRequirements', 'Requisitos del logo:')}
                        </p>
                        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                          <li>{t('business.logoFormatPng', 'Formato: PNG únicamente')}</li>
                          <li>{t('business.logoMaxSize', 'Tamaño máximo: 2MB')}</li>
                          <li>{t('business.logoRecommendation', 'Recomendado: Fondo transparente, 500x500px')}</li>
                        </ul>
                      </div>
                      
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <p className="text-sm text-yellow-800">
                          {t('business.logoHelp', 'Puede agregar el logo del cliente para personalizar documentos y reportes.')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={handleCloseEditModal}
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  {t('common.cancel', 'Cancelar')}
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={saving || !editFormData.name || !editFormData.email}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {t('common.saving', 'Guardando...')}
                    </>
                  ) : (
                    <>
                      <FaSave />
                      {t('common.save', 'Guardar')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de visualización de contacto */}
        {showContactModal && selectedContact && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-2xl p-8 w-full max-w-lg shadow-2xl">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">
                  {t('business.contactDetails', 'Detalles del contacto')}
                </h3>
                <button
                  onClick={handleCloseContactModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FaTimes size={24} />
                </button>
              </div>

              {/* Tabs del modal de contacto */}
              <div className="flex border-b border-gray-200 mb-6">
                <button
                  onClick={() => setContactModalTab('info')}
                  className={`px-6 py-3 font-medium transition-all ${
                    contactModalTab === 'info'
                      ? 'border-b-2 border-red-500 text-red-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('business.contactInfo', 'Información')}
                </button>
                {selectedContact.linkedinUrl && (
                  <button
                    onClick={() => setContactModalTab('linkedin')}
                    className={`px-6 py-3 font-medium transition-all ${
                      contactModalTab === 'linkedin'
                        ? 'border-b-2 border-red-500 text-red-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    LinkedIn
                  </button>
                )}
              </div>

              {/* Contenido del tab */}
              <div className="space-y-4">
                {contactModalTab === 'info' && (
                  <>
                    {!editingContact ? (
                      <>
                        <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-lg p-6 border border-red-100">
                          <div className="text-center mb-4">
                            <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                              <span className="text-3xl text-white font-bold">
                                {selectedContact.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <h4 className="text-xl font-bold text-gray-900">{selectedContact.name}</h4>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="bg-gray-50 rounded-lg p-4">
                            <label className="block text-sm font-medium text-gray-600 mb-2">
                              {t('business.contactEmail', 'Email')}
                            </label>
                            <div className="flex items-center gap-2">
                              <span className="text-lg">📧</span>
                              <a
                                href={`mailto:${selectedContact.email}`}
                                className="text-blue-600 hover:underline font-medium"
                              >
                                {selectedContact.email}
                              </a>
                            </div>
                          </div>

                          {selectedContact.phone && (
                            <div className="bg-gray-50 rounded-lg p-4">
                              <label className="block text-sm font-medium text-gray-600 mb-2">
                                {t('business.contactPhone', 'Teléfono')}
                              </label>
                              <div className="flex items-center gap-2">
                                <span className="text-lg">📱</span>
                                <a
                                  href={`tel:${selectedContact.phone}`}
                                  className="text-blue-600 hover:underline font-medium"
                                >
                                  {selectedContact.phone}
                                </a>
                              </div>
                            </div>
                          )}

                          {selectedContact.linkedinUrl && (
                            <div className="bg-gray-50 rounded-lg p-4">
                              <label className="block text-sm font-medium text-gray-600 mb-2">
                                LinkedIn
                              </label>
                              <div className="flex items-center gap-2">
                                <FaLinkedin className="text-blue-600 text-xl" />
                                <a
                                  href={selectedContact.linkedinUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline font-medium"
                                >
                                  {t('business.viewProfile', 'Ver perfil')}
                                </a>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Botones de acción */}
                        <div className="flex gap-3 pt-4 border-t border-gray-200">
                          <button
                            onClick={handleEditContact}
                            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                          >
                            <FaEdit />
                            {t('common.edit', 'Editar')}
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(t('business.confirmDeleteContact', '¿Eliminar este contacto?'))) {
                                handleRemoveContact(selectedContact.id);
                                handleCloseContactModal();
                              }
                            }}
                            className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                          >
                            <FaTrash />
                            {t('common.delete', 'Eliminar')}
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Modo edición */}
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('business.contactName', 'Nombre')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={editContactData?.name || ''}
                              onChange={(e) => setEditContactData(editContactData ? { ...editContactData, name: e.target.value } : null)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('business.contactEmail', 'Email')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              value={editContactData?.email || ''}
                              onChange={(e) => setEditContactData(editContactData ? { ...editContactData, email: e.target.value } : null)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('business.contactPhone', 'Teléfono')}
                            </label>
                            <input
                              type="tel"
                              value={editContactData?.phone || ''}
                              onChange={(e) => setEditContactData(editContactData ? { ...editContactData, phone: e.target.value } : null)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('business.contactLinkedin', 'LinkedIn URL')}
                            </label>
                            <input
                              type="url"
                              value={editContactData?.linkedinUrl || ''}
                              onChange={(e) => setEditContactData(editContactData ? { ...editContactData, linkedinUrl: e.target.value } : null)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            />
                          </div>
                        </div>

                        {/* Botones de guardar/cancelar */}
                        <div className="flex gap-3 pt-4 border-t border-gray-200">
                          <button
                            onClick={handleCancelContactEdit}
                            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                          >
                            {t('common.cancel', 'Cancelar')}
                          </button>
                          <button
                            onClick={handleSaveContactEdit}
                            disabled={!editContactData?.name || !editContactData?.email}
                            className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            <FaSave />
                            {t('common.save', 'Guardar')}
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}

                {contactModalTab === 'linkedin' && selectedContact.linkedinUrl && (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                      <FaLinkedin className="text-6xl text-blue-600 mx-auto mb-4" />
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">
                        {selectedContact.name}
                      </h4>
                      <p className="text-sm text-gray-600 mb-4">
                        {t('business.linkedinProfileMessage', 'Perfil profesional en LinkedIn')}
                      </p>
                      <a
                        href={selectedContact.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
                      >
                        <FaLinkedin />
                        {t('business.openLinkedIn', 'Abrir en LinkedIn')}
                      </a>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-600 mb-2">URL</label>
                      <div className="bg-white border border-gray-300 rounded px-3 py-2 text-sm text-gray-700 break-all">
                        {selectedContact.linkedinUrl}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {!editingContact && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <button
                    onClick={handleCloseContactModal}
                    className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    {t('common.close', 'Cerrar')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Clients; 